import { Component, Input, OnInit } from '@angular/core';
import { ViewStatus } from '@uranda/ui';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ControlPanelService } from '../control-panel.service';
import { AlertNotificationsService } from '../../../../../services/alert-notifications.service';
import { SubscriptionService } from '../../../../../services/subscription.service';
import { WalletService } from '../../../../../services/wallet.service';
import { AccountSubscription, SubscriptionPlan, SubscriptionStatus } from '../../../../models/subscription.model';
import { AmountUtils } from '../../../../../services/utils/format-amounts.utils';
import { isHandledPermissionError } from '../../../../interceptor/http-interceptor';

@Component({
  selector: 'app-partner-subscriptions',
  templateUrl: './partner-subscriptions.component.html',
  styleUrls: ['./partner-subscriptions.component.scss'],
  standalone: false,
})
export class PartnerSubscriptionsComponent implements OnInit {
  ViewStatus = ViewStatus;
  SubscriptionStatus = SubscriptionStatus;
  AmountUtils = AmountUtils;
  viewStatus = ViewStatus.LOADING;
  plans: SubscriptionPlan[] = [];

  @Input() forcedMode = false;

  activeSubscriptionMap: Map<number, AccountSubscription> = new Map();
  processingPlanId: number | null = null;
  togglingRenewalPlanId: number | null = null;
  planToDisableRenewal: SubscriptionPlan | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private subscriptionService: SubscriptionService,
    private notifications: AlertNotificationsService,
    private modalService: NgbModal,
    private walletService: WalletService,
  ) {}

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    try {
      this.viewStatus = ViewStatus.LOADING;
      const [plans, activeSubscriptions] = await Promise.all([
        this.subscriptionService.getPlans(),
        this.subscriptionService.getActiveSubscriptions(),
      ]);
      this.plans = plans;
      this.activeSubscriptionMap = new Map(
        activeSubscriptions.map(s => [s.plan.id, s])
      );
      this.viewStatus = ViewStatus.READY;
    } catch (e) {
      console.error(e);
      this.viewStatus = ViewStatus.ERROR;
    }
  }

  getSubscription(plan: SubscriptionPlan): AccountSubscription | undefined {
    return this.activeSubscriptionMap.get(plan.id);
  }



  /** Il piano di default (Free) non è cancellabile lato backend, quindi non offre il toggle. */
  canManageRenewal(plan: SubscriptionPlan): boolean {
    if (plan.isDefault) return false;
    const sub = this.getSubscription(plan);
    return !!sub && (
      sub.status === SubscriptionStatus.TRIAL ||
      sub.status === SubscriptionStatus.ACTIVE ||
      sub.status === SubscriptionStatus.PENDING_CANCELLATION
    );
  }

  /**
   * Lo switch è acceso solo se la subscription rinnoverà davvero: PENDING_CANCELLATION non rinnova,
   * e una subscription assegnata dall'admin può essere ACTIVE con autoRenewal già false.
   */
  isRenewalOn(sub: AccountSubscription): boolean {
    return sub.status !== SubscriptionStatus.PENDING_CANCELLATION && !!sub.autoRenewal;
  }

  /**
   * Il backend non azzera mai trialEndsAt, quindi la sola presenza del campo non basta:
   * il trial è in corso solo se la data è ancora futura.
   */
  isTrialPeriodActive(sub: AccountSubscription): boolean {
    return !!sub.trialEndsAt && new Date(sub.trialEndsAt).getTime() > Date.now();
  }

  renewalHint(sub: AccountSubscription): string {
    if (!this.isRenewalOn(sub)) {
      return this.isTrialPeriodActive(sub)
        ? 'Trial will end without any charge'
        : 'Subscription will not renew';
    }
    return this.isTrialPeriodActive(sub)
      ? 'Trial converts to a paid subscription automatically'
      : 'Subscription renews automatically';
  }

  async subscribe(plan: SubscriptionPlan) {
    this.processingPlanId = plan.id;
    try {
      await this.subscriptionService.subscribeToPlan(plan.id);
      this.notifications.success(`Successfully subscribed to ${plan.name}!`);
      if (this.forcedMode) {
        this.forcedMode = false;
      }
      // Un piano a pagamento addebita subito il wallet: lo ricarichiamo senza dipendere solo dalla
      // notifica BILLING via websocket (subscribe è owner-only, quindi /v1/wallet/debt è permesso).
      this.walletService.getWallet().catch(e => console.error('Error refreshing wallet:', e));
      await this.loadData();
    } catch (err: any) {
      // A PERMISSION_REQUIRED 403 already shows its own warning toast (see http-interceptor).
      if (!isHandledPermissionError(err)) {
        const status = err?.status;
        if (status === 400) {
          this.notifications.error('Insufficient funds in your wallet.');
        } else if (status === 409) {
          this.notifications.warning('You are already subscribed to this plan.');
        } else {
          this.notifications.error('An error occurred. Please try again.');
        }
      }
    } finally {
      this.processingPlanId = null;
    }
  }

  /**
   * Lo switch è l'unico controllo della subscription:
   * - off  → cancella (la subscription resta usabile fino a expiresAt, poi scade);
   * - on   → ripristina una cancellazione pendente, oppure riattiva il solo rinnovo
   *          su una subscription ancora attiva ma con autoRenewal spento.
   */
  async onRenewalToggle(plan: SubscriptionPlan, event: Event, confirmTemplate: any) {
    const input = event.target as HTMLInputElement;
    const sub = this.getSubscription(plan);
    if (!sub) return;

    const turningOff = this.isRenewalOn(sub);
    // La posizione dello switch è pilotata dal DOM, non da [checked]: se l'operazione non va a buon
    // fine va rimessa a mano dov'era, altrimenti resta a mentire finché non si ricarica la modale.
    const restoreSwitch = () => { input.checked = turningOff; };

    if (turningOff && !(await this.confirmDisableRenewal(plan, confirmTemplate))) {
      restoreSwitch();
      return;
    }

    this.togglingRenewalPlanId = plan.id;
    try {
      if (turningOff) {
        await this.subscriptionService.cancelSubscription(plan.id);
        this.notifications.success(
          this.isTrialPeriodActive(sub)
            ? 'Auto-renewal turned off. Your trial will end without any charge.'
            : 'Auto-renewal turned off. Your subscription will not renew.'
        );
      } else if (sub.status === SubscriptionStatus.PENDING_CANCELLATION) {
        await this.subscriptionService.reactivateSubscription(plan.id);
        this.notifications.success(`Subscription to ${plan.name} restored.`);
      } else {
        await this.subscriptionService.setAutoRenewal(plan.id, true);
        this.notifications.success('Auto-renewal turned on.');
      }
      await this.loadData();
    } catch (error) {
      restoreSwitch();
      // A PERMISSION_REQUIRED 403 already shows its own warning toast (see http-interceptor).
      if (!isHandledPermissionError(error)) {
        this.notifications.error('An error occurred. Please try again.');
      }
    } finally {
      this.togglingRenewalPlanId = null;
    }
  }

  private confirmDisableRenewal(plan: SubscriptionPlan, content: any): Promise<boolean> {
    this.planToDisableRenewal = plan;
    return this.modalService.open(content, { centered: true }).result.then(
      () => { this.planToDisableRenewal = null; return true; },
      () => { this.planToDisableRenewal = null; return false; },
    );
  }

  /**
   * Il listino va etichettato per tipo di prodotto, non con `productDescription`: quella colonna
   * è il nome interno del modello dati ("Quote Request") e non dice alla clinica cosa sta
   * comprando — per giunta suggerisce che a costare sia la richiesta, mentre si paga lo sblocco
   * del numero. La descrizione resta come fallback per prodotti non ancora mappati qui.
   */
  private static readonly PRODUCT_LABELS: Record<string, string> = {
    CONTACT_REQUEST: "Unlock a patient's phone number",
    QUOTE_REQUEST: 'Unlock the number after a patient shows interest in your quote',
    QUOTE_REQUEST_ACCEPTANCE: 'Accept a quote request and send your quote',
  };

  productLabel(pp: any): string {
    return PartnerSubscriptionsComponent.PRODUCT_LABELS[pp.productType] ?? pp.productDescription;
  }

  /**
   * L'accettazione è gratuita solo per le cliniche che, in quel ciclo di distribuzione, rientrano
   * nell'algoritmo di selezione (flag `free_acceptance` sull'assignment). Mostrare il solo importo
   * o un secco "Free" darebbe in entrambi i casi un'informazione falsa.
   */
  isConditionalPrice(pp: any): boolean {
    return pp?.productType === 'QUOTE_REQUEST_ACCEPTANCE';
  }

  hasConditionalPrice(plan: SubscriptionPlan): boolean {
    return (plan.productPrices ?? []).some((pp) => this.isConditionalPrice(pp));
  }

  productPriceLabel(pp: any): string {
    const amount = this.formatProductPrice(pp);
    if (!this.isConditionalPrice(pp)) return amount;
    // Listino a zero: è gratuita per tutti, la condizione non ha più nulla da esprimere.
    return amount === 'Free' ? 'Included' : `Included or ${amount}`;
  }

  formatProductPrice(pp: any): string {
    const cost = parseFloat(pp.cost?.source ?? pp.cost ?? '0');
    const pct = parseFloat(pp.percentage?.source ?? pp.percentage ?? '0');
    const parts: string[] = [];
    if (cost > 0) parts.push(AmountUtils.formatAmount(cost, 'EUR'));
    if (pct > 0) parts.push(`${pct}%`);
    return parts.length ? parts.join(' + ') : 'Free';
  }

  formatPrice(price: any): string {
    if (price == null) return '—';
    const val = typeof price === 'number'
      ? price
      : parseFloat(price.source ?? price.parsedValue ?? '0');
    return AmountUtils.formatAmount(val, 'EUR');
  }

  /**
   * Il badge segue il periodo di prova reale, non il solo status: una subscription può essere ACTIVE
   * con il trial ancora in corso (righe create prima del fix a reactivateSubscription).
   * PENDING_CANCELLATION conserva il proprio badge: dice che il trial non si convertirà.
   */
  private showsTrialBadge(sub: AccountSubscription): boolean {
    return this.isTrialPeriodActive(sub) && (
      sub.status === SubscriptionStatus.TRIAL || sub.status === SubscriptionStatus.ACTIVE
    );
  }

  badgeClass(sub: AccountSubscription): string {
    return this.showsTrialBadge(sub) ? 'bg-info' : this.statusBadgeClass(sub.status);
  }

  badgeLabel(sub: AccountSubscription): string {
    return this.showsTrialBadge(sub) ? 'Trial Period' : this.statusBadgeLabel(sub.status);
  }

  statusBadgeClass(status: SubscriptionStatus): string {
    const map: Record<SubscriptionStatus, string> = {
      [SubscriptionStatus.TRIAL]: 'bg-info',
      [SubscriptionStatus.ACTIVE]: 'bg-success',
      [SubscriptionStatus.CANCELLED]: 'bg-warning',
      [SubscriptionStatus.SUSPENDED]: 'bg-danger',
      [SubscriptionStatus.EXPIRED]: 'bg-secondary',
      [SubscriptionStatus.PENDING_CANCELLATION]: 'bg-warning',
    };
    return map[status] ?? 'bg-secondary';
  }

  statusBadgeLabel(status: SubscriptionStatus): string {
    const map: Record<SubscriptionStatus, string> = {
      [SubscriptionStatus.TRIAL]: 'Trial',
      [SubscriptionStatus.ACTIVE]: 'Active',
      [SubscriptionStatus.CANCELLED]: 'Cancelled',
      [SubscriptionStatus.SUSPENDED]: 'Suspended',
      [SubscriptionStatus.EXPIRED]: 'Expired',
      [SubscriptionStatus.PENDING_CANCELLATION]: 'Pending Cancellation',
    };
    return map[status] ?? '';
  }
}
